<template>
  <div :class="$style.root">
    <label
      :class="[
        $style.label,
        {
          [$style.appearanceTop]: appearance === 'top',
          [$style.highlighted]: highlited,
        },
      ]"
      :for="htmlFor"
    >
      <div :class="$style.labelContent">
        <span
          :id="uniqueId"
          ref="textRef"
          v-tooltip="tooltipId"
          :class="[$style.labelText, { [$style.required]: required }]"
        >
          <slot />
        </span>
        <span v-if="props.optionalText || props.optional" :class="$style.optional">
          {{ optionalText ?? $t("common.label.optional") }}
        </span>
        <div v-if="hasLabelIcon" :class="$style.labelIcon">
          <slot name="labelIcon" />
        </div>
      </div>
      <SCATooltip
        v-if="tooltipType === 'default' && (hasTooltip || isOverflow)"
        :id="uniqueId"
        :text="hasTooltip ? (tooltip ?? '') : labelText"
        :help-link="hasTooltip ? helpLink : undefined"
      />
    </label>
    <div
      v-if="$slots.labelActionLabel"
      :class="[$style.labelActionContainer, labelActionSlotCount < 2 ? $style.noGap : '']"
    >
      <slot name="labelActionLabel" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Slot, VNode } from "vue";
import { computed, onMounted, onUpdated, ref, useSlots } from "vue";

import SCATooltip from "./SCATooltip.vue";
import { useIsTruncated } from "./useIsTruncated";

const props = withDefaults(
  defineProps<{
    appearance?: "top" | "typo-header-xlarge";
    optional?: boolean;
    optionalText?: string;
    required?: boolean;
    htmlFor?: string;
    highlited?: boolean;
    tooltipType?: "default" | "details";
    tooltipId?: string;
    tooltip?: string;
    helpLink?: string;
  }>(),
  {
    appearance: undefined,
    optional: false,
    optionalText: undefined,
    required: false,
    htmlFor: undefined,
    highlited: false,
    tooltipType: "default",
    tooltipId: undefined,
    tooltip: undefined,
    helpLink: undefined,
  },
);

const hasTooltip = computed(() => !!props.tooltip?.trim());

const textRef = ref<HTMLElement | null>(null);
const labelText = ref("");

function readLabelText() {
  labelText.value = textRef.value?.textContent ?? "";
}

onMounted(readLabelText);
onUpdated(readLabelText);

// Observing the text itself also catches a label that mounted hidden (v-show tab, opening dialog).
const isOverflow = useIsTruncated(textRef, labelText);

const slots = useSlots();
const uniqueId = `lbl-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;

const hasLabelIcon = computed(() => {
  const slotFn: Slot | undefined = slots.labelIcon;
  if (!slotFn) return false;

  const vnodes: VNode[] = slotFn({});

  return vnodes.some((vnode) => {
    if (!vnode) return false;
    if (Array.isArray(vnode.children) && vnode.children.length === 0) return false;
    return true;
  });
});

const labelActionSlotCount = computed(() => {
  const slot = slots.labelActionLabel?.({});
  if (!slot) return 0;

  return slot.filter((vnode: VNode) => vnode.type !== Comment).length;
});
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.root {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.labelActionContainer {
  display: flex;
  gap: var(--space-component-gap-xSmall);
  padding-left: var(--space-component-gap-xSmall);
}

.noGap {
  gap: 0 !important;
  padding-left: 0 !important;
}

.label {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-disabled);
  width: auto;
  min-width: 0;
  position: relative;
  text-align: var(--sca-label-text-align, left);
  flex-grow: 1;
  justify-content: var(--sca-label-text-align, left);

  .labelContent {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-component-padding-xSmall) var(--space-component-padding-null);
  }

  .labelText {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .optional {
    position: relative;
    color: var(--color-content-tertiary);
    display: inline;
  }

  .labelIcon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &.highlighted {
    color: var(--color-interaction-default-normal);

    .labelText,
    .optional,
    .labelIcon {
      color: var(--color-interaction-default-normal);
    }

    svg {
      @include IconMixins.icon-color(var(--color-interaction-default-normal));
    }
  }
}
.appearanceTop {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-primary);
  display: flex;
  gap: var(--space-component-gap-xSmall);
}
</style>
